import React, { useState, useMemo } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { X, Search, Building } from 'lucide-react';
import { useContacts } from '../../hooks/useContacts';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import type { Contact } from '../../types/contact';

interface SelectContactsPopupProps {
  onClose: () => void;
  onSave: (selectedContacts: Contact[]) => void;
  currentlySelectedIds?: string[];
}

export default function SelectContactsPopup({
  onClose,
  onSave,
  currentlySelectedIds = []
}: SelectContactsPopupProps) {
  useEscapeKey(onClose);
  const { contacts, loading } = useContacts();
  const { options: companyOptions, loading: companiesLoading } = useCompanyOptions();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(
    new Set(currentlySelectedIds)
  );

  // Filter contacts based on search and company filter
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const matchesSearch = searchTerm === '' || 
        `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCompany = !selectedCompany || 
        (selectedCompany === 'no_company' ? !contact.company_id : contact.company_id === selectedCompany);
      
      return matchesSearch && matchesCompany;
    });
  }, [contacts, searchTerm, selectedCompany]);

  const toggleContact = (contactId: string) => {
    const newSelected = new Set(selectedContactIds);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContactIds(newSelected);
  };

  const handleSave = () => {
    const selectedContacts = contacts.filter(contact => 
      selectedContactIds.has(contact.id)
    );
    onSave(selectedContacts);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white">Select Contacts</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-dark-600 space-y-4">
          <div className="flex space-x-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* Company Filter */}
            <div className="w-64">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="input w-full"
                disabled={companiesLoading}
              >
                <option value="">All Companies</option>
                <option value="no_company">No Company Assigned</option>
                {companyOptions.map(company => (
                  <option key={company.value} value={company.value}>
                    {company.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Contact List */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-gray-400">Loading contacts...</div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-6 text-center text-gray-400">No contacts found</div>
          ) : (
            <div className="divide-y divide-dark-600">
              {filteredContacts.map(contact => (
                <label
                  key={contact.id}
                  className="flex items-center p-4 hover:bg-dark-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedContactIds.has(contact.id)}
                    onChange={() => toggleContact(contact.id)}
                    className="checkbox mr-4"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-3">
                      <p className="text-white font-medium truncate">
                        {contact.first_name} {contact.last_name}
                      </p>
                      {contact.company && (
                        <div className="flex items-center text-sm text-gray-400">
                          <Building className="w-4 h-4 mr-1" />
                          {contact.company.name}
                        </div>
                      )}
                    </div>
                    {(contact.work_email || contact.personal_email) && (
                      <p className="text-sm text-gray-400 truncate">
                        {contact.work_email || contact.personal_email}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-dark-600 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={selectedContactIds.size === 0}
          >
            Add Selected Contacts
          </button>
        </div>
      </div>
    </div>
  );
}
